// src/admin/pages/tabs/DealroomsListTab.js
import React, { useState, useEffect } from 'react';
import { Database, User, FileText, Calendar, HardDrive } from 'lucide-react';
import { supabase } from '../../../config';

const DealroomsListTab = () => {
  const [dealrooms, setDealrooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDealrooms();
  }, []);

  const loadDealrooms = async () => {
    try {
      setLoading(true);
      
      // Fetch ALL dataroom projects (including those without created_by)
      const { data: projectsData, error: projectsError } = await supabase
        .from('dataroom_projects')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10000);
      
      if (projectsError) throw projectsError;

      // Get creator info using created_by field and fallback to dataroom_users
      const projectIds = (projectsData || []).map(p => p.id);
      let creatorsMap = {};
      
      if (projectIds.length > 0) {
        // First, try to get creators from created_by field
        const projectsWithCreatedBy = (projectsData || []).filter(p => p.created_by);
        const creatorIds = [...new Set(projectsWithCreatedBy.map(p => p.created_by).filter(Boolean))];
        
        if (creatorIds.length > 0) {
          // Fetch user profiles for creators from created_by
          const { data: userProfilesFromCreatedBy } = await supabase
            .from('user_profiles')
            .select('id, first_name, last_name, email')
            .in('id', creatorIds);
          
          if (userProfilesFromCreatedBy) {
            const userProfilesMap = {};
            userProfilesFromCreatedBy.forEach(profile => {
              userProfilesMap[profile.id] = profile;
            });
            
            // Map creators to projects using created_by
            projectsData.forEach(p => {
              if (p.created_by && userProfilesMap[p.created_by]) {
                creatorsMap[p.id] = [userProfilesMap[p.created_by]];
              }
            });
          }
        }
        
        // Fallback: Get creators from dataroom_users for projects without created_by
        const projectsWithoutCreator = projectIds.filter(id => !creatorsMap[id]);
        
        if (projectsWithoutCreator.length > 0) {
          const { data: creatorUsers, error: creatorError } = await supabase
            .from('dataroom_users')
            .select('project_id, user_id')
            .in('project_id', projectsWithoutCreator)
            .eq('role', 'creator');
          
          if (creatorError) {
            console.error('Error fetching creators from dataroom_users:', creatorError);
          }
          
          if (creatorUsers && creatorUsers.length > 0) {
            const userIds = [...new Set(creatorUsers.map(cu => cu.user_id).filter(Boolean))];
            
            if (userIds.length > 0) {
              const { data: userProfiles } = await supabase
                .from('user_profiles')
                .select('id, first_name, last_name, email')
                .in('id', userIds);
              
              if (userProfiles) {
                const userProfilesMap = {};
                userProfiles.forEach(profile => {
                  userProfilesMap[profile.id] = profile;
                });
                
                creatorUsers.forEach(cu => {
                  if (!creatorsMap[cu.project_id]) {
                    creatorsMap[cu.project_id] = [];
                  }
                  if (userProfilesMap[cu.user_id]) {
                    creatorsMap[cu.project_id].push(userProfilesMap[cu.user_id]);
                  }
                });
              }
            }
          }
        }
      }
      
      // Enrich ALL projects with creator info (projects without creator will have null)
      const projects = (projectsData || []).map(p => ({
        ...p,
        creator: creatorsMap[p.id]?.[0] || null
      }));

      // Get file counts, user counts, and file sizes for each project
      const projectsWithCounts = await Promise.all(
        (projects || []).map(async (project) => {
          // Get file count
          const { count: fileCount, error: countError } = await supabase
            .from('dataroom_items')
            .select('*', { count: 'exact', head: true })
            .eq('project_id', project.id)
            .eq('is_folder', false);

          // Get viewer count (users with role='viewer')
          const { count: viewerCount, error: viewerError } = await supabase
            .from('dataroom_users')
            .select('*', { count: 'exact', head: true })
            .eq('project_id', project.id)
            .eq('role', 'viewer')
            .eq('status', 'active');

          // Get total file size
          const { data: items, error: sizeError } = await supabase
            .from('dataroom_items')
            .select('file_size')
            .eq('project_id', project.id)
            .eq('is_folder', false);

          const totalSize = items?.reduce((sum, item) => sum + (parseInt(item.file_size) || 0), 0) || 0;

          if (countError) {
            console.error('Error counting files:', countError);
          }
          if (viewerError) {
            console.error('Error counting viewers:', viewerError);
          }
          if (sizeError) {
            console.error('Error getting file sizes:', sizeError);
          }

          return { 
            ...project, 
            file_count: fileCount || 0,
            viewer_count: viewerCount || 0,
            total_file_size: totalSize
          };
        })
      );

      const finalDealrooms = Array.isArray(projectsWithCounts) ? projectsWithCounts : [];
      console.log(`Loaded ${finalDealrooms.length} dealroom projects`);
      setDealrooms(finalDealrooms);
    } catch (error) {
      console.error('Error loading dealrooms:', error);
      setDealrooms([]);
    } finally {
      setLoading(false);
    }
  };

  const getCreatorName = (dealroom) => {
    if (dealroom.creator) {
      if (dealroom.creator.first_name && dealroom.creator.last_name) {
        return `${dealroom.creator.first_name} ${dealroom.creator.last_name}`;
      }
      return dealroom.creator.first_name || dealroom.creator.last_name || dealroom.creator.email || 'Unknown';
    }
    return 'Unknown';
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dealrooms...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">All Dealrooms</h2>
          <p className="text-gray-600 mt-1">View all dataroom projects</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Database className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Dealrooms</p>
              <p className="text-2xl font-bold text-gray-900">{dealrooms.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Files</p>
              <p className="text-2xl font-bold text-gray-900">
                {(Array.isArray(dealrooms) ? dealrooms : []).reduce((sum, d) => sum + (d.file_count || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <User className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Viewers</p>
              <p className="text-2xl font-bold text-gray-900">
                {(Array.isArray(dealrooms) ? dealrooms : []).reduce((sum, d) => sum + (d.viewer_count || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <HardDrive className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Storage</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatFileSize((Array.isArray(dealrooms) ? dealrooms : []).reduce((sum, d) => sum + (d.total_file_size || 0), 0))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Dealrooms Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dealroom Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Files
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Viewers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(dealrooms) && dealrooms.map((dealroom) => (
                <tr key={dealroom.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div 
                          className="h-10 w-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: dealroom.color || '#3B82F6' }}
                        >
                          <Database className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {dealroom.project_name || 'Unnamed Dealroom'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {dealroom.description || 'No description'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-gray-400 mr-1" />
                      {getCreatorName(dealroom)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 text-gray-400 mr-1" />
                      {dealroom.file_count || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-gray-400 mr-1" />
                      {dealroom.viewer_count || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <HardDrive className="h-4 w-4 text-gray-400 mr-1" />
                      {formatFileSize(dealroom.total_file_size || 0)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                      {dealroom.created_at 
                        ? new Date(dealroom.created_at).toLocaleDateString()
                        : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      dealroom.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : dealroom.status === 'archived'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {dealroom.status || 'active'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {dealrooms.length === 0 && (
          <div className="text-center py-12">
            <Database className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No dealrooms</h3>
            <p className="mt-1 text-sm text-gray-500">No dataroom projects have been created yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealroomsListTab;

